/**
 * @fileoverview API service and React Query hooks for Shopify product import.
 * @module features/shopify-import/api/shopifyImportService
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'

import apiClient from '@/lib/apiClient'
import {
  ShopifyStoreArraySchema,
  ShopifyImportPreviewResponseSchema,
  ShopifyImportResponseSchema,
  type ShopifyStore,
  type ShopifyImportPreviewResponse,
  type ShopifyImportRequest,
  type ShopifyImportResponse,
} from '../schemas/shopifyImportSchema'

export const SHOPIFY_STORES_QUERY_KEY = ['shopify', 'import-stores'] as const
export const SHOPIFY_IMPORT_PREVIEW_QUERY_KEY = ['shopify', 'import-preview'] as const

/**
 * Fetch available Shopify stores with DIRECT connection mode.
 * @returns Array of stores available for product import
 */
export async function getShopifyStores(): Promise<ShopifyStore[]> {
  const res = await apiClient.get('/shopify/products/stores')
  return ShopifyStoreArraySchema.parse(res.data)
}

/**
 * Fetch import preview for a specific Shopify store connection.
 * @param connectionId Shopify connection UUID
 * @returns Preview of products to import
 */
export async function getShopifyImportPreview(
  connectionId: string
): Promise<ShopifyImportPreviewResponse> {
  const res = await apiClient.get(`/shopify/products/preview/${connectionId}`)
  return ShopifyImportPreviewResponseSchema.parse(res.data)
}

/**
 * Execute product import from Shopify store.
 * @param connectionId Shopify connection UUID
 * @param request Import request with optional product IDs
 * @returns Import result summary
 */
export async function importShopifyProducts(
  connectionId: string,
  request: ShopifyImportRequest
): Promise<ShopifyImportResponse> {
  const res = await apiClient.post(`/shopify/products/import/${connectionId}`, request)
  return ShopifyImportResponseSchema.parse(res.data)
}

/**
 * React Query hook for fetching available Shopify stores.
 * @param enabled Whether the query should be enabled
 * @returns Query result with stores
 */
export function useShopifyStoresQuery(enabled = true): UseQueryResult<ShopifyStore[], Error> {
  return useQuery<ShopifyStore[], Error>({
    queryKey: SHOPIFY_STORES_QUERY_KEY,
    queryFn: getShopifyStores,
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * React Query hook for fetching Shopify import preview.
 * @param connectionId Shopify connection UUID
 * @param enabled Whether the query should be enabled
 * @returns Query result with import preview
 */
export function useShopifyImportPreviewQuery(
  connectionId: string | null,
  enabled = true
): UseQueryResult<ShopifyImportPreviewResponse, Error> {
  return useQuery<ShopifyImportPreviewResponse, Error>({
    queryKey: [...SHOPIFY_IMPORT_PREVIEW_QUERY_KEY, connectionId],
    queryFn: () => getShopifyImportPreview(connectionId!),
    enabled: enabled && connectionId !== null,
    staleTime: 60 * 1000, // 1 minute
  })
}

/**
 * Mutation variables for Shopify product import.
 */
interface ShopifyImportMutationVariables {
  connectionId: string
  request: ShopifyImportRequest
}

/**
 * React Query mutation hook for importing Shopify products.
 * @returns Mutation result for product import
 */
export function useShopifyImportProductsMutation(): UseMutationResult<
  ShopifyImportResponse,
  Error,
  ShopifyImportMutationVariables
> {
  const queryClient = useQueryClient()

  return useMutation<ShopifyImportResponse, Error, ShopifyImportMutationVariables>({
    mutationFn: ({ connectionId, request }) => importShopifyProducts(connectionId, request),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['products'] }),
        queryClient.invalidateQueries({ queryKey: ['variants'] }),
        queryClient.invalidateQueries({ queryKey: SHOPIFY_IMPORT_PREVIEW_QUERY_KEY }),
      ])
    },
  })
}
